import * as React from 'react';
import ouiBase from '@goldwasserexchange/oui';
import { Locale } from '@goldwasserexchange/i18n';
import { useHookstate } from '@hookstate/core';
import { UncastedDataStructure, dataStructure } from '@goldwasserexchange/oblis-frontend-utils';
import { useFieldErrorByFieldPath, useFieldTouchedByFieldPath } from '../../../../../../OUI/Form';
import { subscribeToMailchimpNewsLetter } from '../../../../../../mailchimp';

type SubmitNewsLetterData = [({ email, firstName, lastName }: any) => void, boolean, string | undefined, boolean];

export const useSubmitNewsletter = (
  language: string,
): SubmitNewsLetterData => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [submitted, setSubmitted] = React.useState<boolean>(false);
  const currentUserIndex = dataStructure.T_ADD.fields.CURRENT.hooks.useCurrentUserIndex();
  const valueState = ouiBase.Form.useFormData<UncastedDataStructure>();
  // first name
  const firstNameState = useHookstate(valueState.onboarding.users[currentUserIndex].FIRSTNAME);
  const firstNamePath = firstNameState.path.join('.');
  const firstNameTouched = useFieldTouchedByFieldPath(firstNamePath);
  const firstNameError = useFieldErrorByFieldPath(firstNamePath);
  // last name
  const lastNameState = useHookstate(valueState.onboarding.users[currentUserIndex].NAME_1);
  const lastNamePath = lastNameState.path.join('.');
  const lastNameTouched = useFieldTouchedByFieldPath(lastNamePath);
  const lastNameError = useFieldErrorByFieldPath(lastNamePath);
  // email
  const emailState = useHookstate(valueState.onboarding.users[currentUserIndex].PHONE[0].PHONE);
  const emailPath = emailState.path.join('.');
  const emailTouched = useFieldTouchedByFieldPath(emailPath);
  const emailError = useFieldErrorByFieldPath(emailPath);
  const submit = React.useCallback(({ email, firstName, lastName }) => {
    if (!loading) {
      setLoading(true);
      firstNameTouched.set(true);
      lastNameTouched.set(true);
      emailTouched.set(true);
      if (firstNameError.get().length === 0 && lastNameError.get().length === 0 && emailError.get().length === 0) {
        setSubmitted(true);
        subscribeToMailchimpNewsLetter({
          language: language as Locale,
          firstName: firstName.get(),
          lastName: lastName.get(),
          email: email.get(),
        })
          .then(() => {
            setError(undefined);
          })
          .catch(() => setError('error'))
          .finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    }
  }, [
    language,
    firstNameTouched,
    lastNameTouched,
    emailTouched,
    firstNameError,
    lastNameError,
    emailError,
    setLoading,
    setSubmitted,
    loading,
  ]);
  const result = React.useMemo(() => [
    submit,
    loading,
    error,
    submitted,
  ], [
    submit,
    loading,
    error,
    submitted,
  ]) as SubmitNewsLetterData;
  React.useEffect(() => {
    if (submitted && !loading && error === undefined) {
      firstNameTouched.set(false);
      lastNameTouched.set(false);
      emailTouched.set(false);
      emailState.set('');
      firstNameState.set('');
      lastNameState.set('');
      setTimeout(() => setSubmitted(false), 3000);
    }
  }, [
    submitted,
    loading,
    error,
  ]);
  return result;
};
